import { useEffect, useRef, useState } from 'react';
import { User, ArrowRight, X, Send, UserPlus } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { extractErr } from '../../api/error-message';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useUrlTab } from '../../services/attention';

// Wired to GET /admin/support/tickets (+ detail, assign, status, reply). Any
// customer / seller / delivery partner can raise a ticket from their app; admins
// triage the queue here.

const STATUS_TABS = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'ALL'] as const;
type StatusKey = (typeof STATUS_TABS)[number];
const NEXT_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];

interface TicketRow {
  id: string;
  ticketNumber: string;
  raisedByActorType: string;
  raisedByActorId: string;
  category: string;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
  assignedAdminId: string | null;
  _count: { messages: number };
}

interface TicketMessage {
  id: string;
  senderActorType: string;
  body: string;
  createdAt: string;
}
interface TicketDetail extends TicketRow {
  description: string | null;
  messages: TicketMessage[];
  assignedAdmin: { id: string; name: string } | null;
  relatedOrderId: string | null;
}

const priorityClass: Record<string, string> = {
  HIGH: 'badge-danger',
  MEDIUM: 'badge-warning',
  LOW: 'badge-success',
};
const statusChip: Record<string, string> = {
  OPEN: 'badge-info',
  IN_PROGRESS: 'badge-warning',
  RESOLVED: 'badge-success',
  CLOSED: '',
};
/** Display-only sentence case: `IN_PROGRESS` → `In progress`. */
const pretty = (s: string) => {
  const lower = s.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export function SupportPage() {
  // In the URL (?tab=) so the dashboard / sidebar can open a specific queue.
  const [tab, setTab] = useUrlTab<StatusKey>(STATUS_TABS, 'OPEN');
  const [q, setQ] = useState('');
  // Server-searched: only the newest 50 tickets are loaded, so the query goes
  // to the API and matches everything a row shows — ticket no., category,
  // subject, who raised it ("customer #42"), date (dd/mm/yyyy), priority, status.
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [rows, setRows] = useState<TicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Only the newest request may write rows: typing fires several, and a slow
  // early one must not overwrite the results of the query now in the box.
  const latestRequest = useRef(0);
  const load = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const { data } = await api.get<{ items: TicketRow[] }>('/admin/support/tickets', {
        params: { status: tab, limit: 50, ...(debouncedQ ? { q: debouncedQ } : {}) },
      });
      if (requestId !== latestRequest.current) return;
      setRows(data.items);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [tab, debouncedQ]);
  const searching = debouncedQ.length > 0;

  const openCount = rows.filter((t) => t.status !== 'RESOLVED' && t.status !== 'CLOSED').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support"
        description={tab === 'ALL' ? `${rows.length} tickets` : `${openCount} in this view`}
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className="fchip"
          >
            {t === 'ALL' ? 'All' : pretty(t)}
          </button>
        ))}
      </div>

      <TableSearch
        value={q}
        onChange={setQ}
        placeholder="Search by ticket, subject, category, raised by, date, priority or status"
        resultCount={!loading && searching ? rows.length : undefined}
      />

      {loading ? (
        <div className="panel overflow-hidden"><SkeletonRows cols={4} /></div>
      ) : rows.length === 0 && searching ? (
        <EmptyState
          icon="search_off"
          title={`No matches for “${debouncedQ}”`}
          body="Try another word, or clear the search to see every ticket in this view."
          action={<button type="button" onClick={() => setQ('')} className="btn btn-secondary">Clear search</button>}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="support_agent"
          title="No tickets in this view"
          body="Tickets raised by customers, sellers and delivery partners from their apps land here."
        />
      ) : (
        <div className="panel overflow-hidden">
          <div className="divide-y divide-surface-line">
            {rows.map((t) => (
              <button key={t.id} type="button" onClick={() => setSelectedId(t.id)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-sunk">
                <span className={`badge shrink-0 ${priorityClass[t.priority] ?? ''}`}>
                  {pretty(t.priority)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex items-center gap-2">
                    <span className="sku font-semibold text-admin-ink">{t.ticketNumber}</span>
                    <span className="rounded-xs bg-surface-2 px-1.5 py-0.5 text-[12px] text-ink-700">{t.category}</span>
                  </div>
                  <p className="truncate text-[14.5px] font-semibold text-ink-900">{t.subject}</p>
                  <div className="mt-0.5 flex items-center gap-3 text-[12.5px] text-ink-500">
                    <span className="flex items-center gap-1"><User size={12} /> {pretty(t.raisedByActorType)} #{t.raisedByActorId}</span>
                    <span>{new Date(t.createdAt).toLocaleString('en-IN')}</span>
                    <span>{t._count.messages} msg</span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  <span className={`badge ${statusChip[t.status] ?? ''}`}>
                    {pretty(t.status)}
                  </span>
                  <ArrowRight size={16} className="text-ink-400" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedId && (
        <TicketPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function TicketPanel({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const { confirm } = useConfirm();

  const fetchDetail = async () => {
    try {
      const { data } = await api.get<TicketDetail>(`/admin/support/tickets/${id}`);
      setTicket(data);
    } catch (err) {
      toast.error(extractErr(err));
    }
  };
  useEffect(() => { void fetchDetail(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status: string) => {
    // Closing ends the conversation: no more replies, and the raiser is told.
    if (status === 'CLOSED') {
      const ok = await confirm({
        title: 'Close ticket?',
        description: `${ticket?.ticketNumber ?? 'This ticket'} stops taking replies and the person who raised it is notified. You can reopen it later from here.`,
        confirmLabel: 'Close ticket',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${id}/status`, { status });
      toast.success(`Ticket ${pretty(status).toLowerCase()}`);
      await fetchDetail();
      onChanged();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const assignToMe = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${id}/assign`);
      toast.success('Assigned to you');
      await fetchDetail();
      onChanged();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) { toast.error('Reply is empty'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${id}/messages`, { body: reply.trim() });
      setReply('');
      toast.success('Reply sent');
      await fetchDetail();
      onChanged();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim" onClick={onClose}>
      <aside className="flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4" onClick={(e) => e.stopPropagation()}>
        {!ticket ? (
          <div className="p-8"><SkeletonRows cols={3} /></div>
        ) : (
          <>
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-surface-line px-6 pb-4 pt-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="sku font-semibold text-admin-ink">{ticket.ticketNumber}</span>
                  <span className={`badge ${priorityClass[ticket.priority] ?? ''}`}>{pretty(ticket.priority)}</span>
                  <span className={`badge ${statusChip[ticket.status] ?? ''}`}>{pretty(ticket.status)}</span>
                </div>
                <h2 className="mt-1.5 line-clamp-2 font-display text-drawer-title leading-tight text-ink-900">{ticket.subject}</h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="btn btn-quiet btn-icon btn-sm"><X size={18} /></button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-2 gap-4 rounded-md bg-surface-sunk p-4">
                <Meta label="Raised by" value={`${pretty(ticket.raisedByActorType)} #${ticket.raisedByActorId}`} />
                <Meta label="Category" value={ticket.category} />
                <Meta label="Created" value={new Date(ticket.createdAt).toLocaleString('en-IN')} />
                <Meta label="Assigned" value={ticket.assignedAdmin?.name ?? 'Unassigned'} />
                {ticket.relatedOrderId && (
                  <div className="col-span-2">
                    <p className="text-[12.5px] font-semibold text-ink-500">Related order</p>
                    <p className="sku mt-0.5 font-semibold text-admin-ink">#{ticket.relatedOrderId}</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2.5 text-[15px] font-semibold text-ink-900">Conversation</h3>
                <div className="space-y-2">
                  {ticket.messages.length === 0 && <p className="text-[13.5px] text-ink-500">No messages yet.</p>}
                  {ticket.messages.map((m) => {
                    const isAdmin = m.senderActorType === 'ADMIN';
                    return (
                      <div key={m.id} className={`rounded-md p-3 text-[14px] ${isAdmin ? 'bg-admin-soft text-ink-900' : 'border border-surface-line text-ink-700'}`}>
                        <p className="mb-0.5 text-[12.5px] text-ink-500">{isAdmin ? 'Support' : pretty(m.senderActorType)} · {new Date(m.createdAt).toLocaleString('en-IN')}</p>
                        {m.body}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="shrink-0 space-y-3 border-t border-surface-line px-6 py-3.5">
              {ticket.status !== 'CLOSED' && (
                <>
                  <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the customer…"
                    aria-label="Reply" className="input resize-none" />
                  <div className="flex flex-wrap justify-end gap-2.5">
                    <button type="button" disabled={busy} onClick={() => void assignToMe()} className="btn btn-secondary btn-sm"><UserPlus size={16} /> Assign to me</button>
                    <button type="button" disabled={busy} onClick={() => void sendReply()} className="btn btn-primary btn-sm"><Send size={16} /> Reply</button>
                  </div>
                </>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-[12.5px] font-semibold text-ink-500">Move to</span>
                {NEXT_STATUSES.filter((s) => s !== ticket.status).map((s) => (
                  <button key={s} type="button" disabled={busy} onClick={() => void setStatus(s)}
                    className="btn btn-quiet btn-sm">
                    → {pretty(s)}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[12.5px] font-semibold text-ink-500">{label}</p>
      <p className="mt-0.5 text-[13.5px] font-medium text-ink-900">{value}</p>
    </div>
  );
}
